import { economyConfig } from '../config/economyConfig';
import { DAYS_PER_MONTH } from '../config/constants';
import { BUILDINGS } from '../data/buildings';
import { getDistrict } from '../data/districts';
import type { ParkingLot } from '../models/ParkingLot';
import type { Vehicle } from '../models/Vehicle';
import { roundMoney } from '../utils/math';
import { ParkingLotEngine } from './ParkingLotEngine';
import { StaffEngine } from './StaffEngine';
import { UpgradeEngine } from './UpgradeEngine';

export interface ExpenseBreakdown {
  rent: number;
  maintenance: number;
  electricity: number;
  system: number;
  staff: number;
  total: number;
}

/**
 * EconomyEngine (spec sections 12, 13, 30): pure money math.
 *
 * Reads all balance numbers from economyConfig / data (never hardcoded), so the
 * game can be tuned without touching logic. No Phaser, no state mutation — it
 * only computes; callers apply the results.
 */
export const EconomyEngine = {
  /**
   * Parking fee for a completed visit. MVP is a simple hourly rate with the
   * hours rounded up (a started hour is charged). Daily-max / first-hour
   * pricing can be layered on later (spec section 12).
   */
  computeParkingFee(lot: ParkingLot, vehicle: Vehicle): number {
    const hours = Math.max(1, Math.ceil(vehicle.plannedDurationMinutes / 60));
    return roundMoney(hours * lot.parkingPrice);
  },

  /** One game-day of operating expenses for a lot, broken down (spec 13). */
  computeDailyExpenses(lot: ParkingLot): ExpenseBreakdown {
    const district = getDistrict(lot.districtId);
    const rentMultiplier = district?.rentMultiplier ?? 1;

    const rent = (economyConfig.BASE_RENT * rentMultiplier) / DAYS_PER_MONTH;

    // Tile maintenance + upgrade maintenance; technicians reduce it (spec 14).
    const maintenanceReduction = StaffEngine.maintenanceReduction(lot);
    const upgradeMaintenance = UpgradeEngine.aggregate(lot).maintenance;
    const maintenance =
      (ParkingLotEngine.getMaintenanceCost(lot, BUILDINGS) / DAYS_PER_MONTH +
        upgradeMaintenance / DAYS_PER_MONTH) *
      (1 - maintenanceReduction);

    const capacity = ParkingLotEngine.getCapacity(lot);
    const electricity =
      capacity *
      economyConfig.ELECTRICITY_KWH_PER_SPACE_DAY *
      economyConfig.ELECTRICITY_RATE;

    const system = economyConfig.SYSTEM_FEE / DAYS_PER_MONTH;

    // Staff salaries are monthly; charge the daily share (spec section 14).
    const staff = StaffEngine.totalSalary(lot) / DAYS_PER_MONTH;

    const total = rent + maintenance + electricity + system + staff;

    return {
      rent: roundMoney(rent),
      maintenance: roundMoney(maintenance),
      electricity: roundMoney(electricity),
      system: roundMoney(system),
      staff: roundMoney(staff),
      total: roundMoney(total),
    };
  },

  /** Total daily expense across all of a player's lots. */
  computeTotalDailyExpense(lots: ReadonlyArray<ParkingLot>): number {
    let total = 0;
    for (const lot of lots) total += this.computeDailyExpenses(lot).total;
    return roundMoney(total);
  },
};
